import type { PropType } from 'vue';
import type { BlockDefinition, BlockPrivateRootContext, BlockRootContext } from '#layers/block-engine/types/block';
import { computed, defineComponent, inject, watch } from 'vue';
import { registerBlockRootContextSource } from '#layers/block-engine/stores/block-root-context';
import { createBlockRootContextApi } from '#layers/block-engine/utils/block/api/root-context';
import { useBlockDefinitionRenderSetup } from './BlockDefinitionRender';
import { blockRenderEmits, blockRootContextApiFallbackKey, shouldRegisterBlockRootContext } from './BlockRenderShared';

const blockRenderComponentProps = {
	block: {
		type: Object as PropType<BlockDefinition>,
		required: true,
	},
	blockId: {
		type: String,
		required: true,
	},
	props: Object,
	spacing: {
		type: String,
		default: '8px',
	},
	unwrapBlockDefinition: {
		type: Boolean,
		default: false,
	},
	context: Object as PropType<BlockRootContext>,
	privateContext: Object as PropType<BlockPrivateRootContext>,
	emitOnRootBlock: Function as PropType<(event: string, payload?: any) => void>,
	registerRootContext: {
		type: Boolean as PropType<boolean | null>,
		default: null,
	},
};

export default defineComponent({
	name: 'BlockRenderComponent',

	props: blockRenderComponentProps,

	emits: blockRenderEmits,

	inheritAttrs: false,

	setup(props, { attrs, slots, emit }) {
		const genericEmit = emit as (event: string, payload?: any) => void;
		const block = computed(() => props.block!);
		const inheritedRootContextApi = inject(blockRootContextApiFallbackKey, null);
		// Parent rerenders are common in dense UIs like data tables. Keep the merged
		// context and vnode prop objects stable so child blocks do not see fresh
		// object identities unless a relevant input actually changed.
		const mergedPrivateRootContext = computed<BlockPrivateRootContext>(() => ({
			...props.privateContext,
			rootBlockOverride: props.privateContext?.rootBlockOverride ?? block.value,
		}));
		const rootContextApi = createBlockRootContextApi({
			getBaseRootContext: () => props.context,
			getRootBlock: () => getPrivateRootContext().rootBlockOverride ?? block.value,
			parentApi: inheritedRootContextApi ?? undefined,
		});
		const blockComponentStyle = computed(() => ({
			'--block-spacing': props.spacing,
			...attrs.style as any,
		}));
		const blockComponentProps = computed(() => ({
			...attrs,
			...props.props,
			'data-block-id': props.blockId,
			'style': blockComponentStyle.value,
		}));

		function getPrivateRootContext(): BlockPrivateRootContext {
			return mergedPrivateRootContext.value;
		}

		const { renderBlock, renderId } = useBlockDefinitionRenderSetup({
			getBlock: () => block.value,
			getComponentProps: () => blockComponentProps.value,
			getDebugProps: () => ({
				...attrs,
				...props.props,
			}),
			slots,
			rootContextApi,
			getPrivateRootContext,
			emitOnRootBlock: props.emitOnRootBlock ?? genericEmit,
			onBlockRef(value) {
				emit('block:ref', value);
			},
			unwrapBlockDefinition: () => props.unwrapBlockDefinition,
			skipErrorBoundary: () => true,
			staleIdWarningSource: 'BlockRenderComponent',
		});

		if (shouldRegisterBlockRootContext({
			registerRootContext: props.registerRootContext,
			inheritedRootContextApi,
		})) {
			registerBlockRootContextSource({
				renderId,
				rootContextApi,
			});
		}

		// Guard against redundant emits when a parent re-renders with the same
		// BlockDefinition reference but unrelated prop changes. Consumers wire
		// analytics/loading indicators off these events, so re-firing them per
		// parent render would cause visible double-counting.
		let lastEmittedBlockId: string | null = null;

		watch(() => props.block, (block) => {
			if (!block || block.id === lastEmittedBlockId) {
				return;
			}

			lastEmittedBlockId = block.id;
			genericEmit('block:resolve', block);
			genericEmit('block:load', block);
		}, {
			immediate: true,
		});

		return renderBlock;
	},
});
